import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { createCycle } from '../store/slices/cyclesSlice';
import toast from 'react-hot-toast';

const INITIAL = {
  start_date: '', end_date: '',
  eligibility_cutoff_months: '',
  lower_cutoff_avg_score: '',
  increment_factor: '',
  low_sal_comp_ctc_limit: '',
  high_sal_reduction_ctc_limit: '',
  low_sal_comp_factor: '',
  high_sal_reduction_factor: '',
  low_sal_comp_score_cutoff: '',
};

const Icon = ({ d }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d={d}/></svg>
);

export default function NewIncrementCyclePage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const errs = {};
    Object.keys(INITIAL).forEach(k => {
      if (!form[k]) errs[k] = 'Required';
    });
    if (form.start_date && form.end_date && form.start_date >= form.end_date)
      errs.end_date = 'End date must be after start date';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    const result = await dispatch(createCycle(form));
    setLoading(false);
    if (result.error) {
      toast.error('Failed to create cycle.');
    } else {
      toast.success(`Cycle ${result.payload.cycle_id} created!`);
      navigate('/increment-cycles');
    }
  };

  const Field = ({ label, name, type = 'number', icon, step = 'any' }) => (
    <div className="form-group">
      <label className="form-label">
        {icon && <Icon d={icon} />}
        {label}
      </label>
      <input
        className={`form-input${errors[name] ? ' error' : ''}`}
        type={type} step={step}
        value={form[name]}
        onChange={set(name)}
        min="0"
      />
      {errors[name] && <span className="form-error">{errors[name]}</span>}
    </div>
  );

  return (
    <div className="page-container">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
        <button
          className="btn-icon magenta"
          onClick={() => navigate(-1)}
          style={{
            width: 34, height: 34, borderRadius: '50%',
            background: 'var(--magenta)', color: 'var(--white)',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
          </svg>
        </button>
        <h1 style={{ fontSize: 22, fontWeight: 700 }}>Create New Increment Cycle</h1>
      </div>

      <div className="card" style={{ maxWidth: 640, padding: 32 }}>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>

          {/* Section 1 */}
          <div>
            <div className="form-section-title">Six-Month Assessment Period</div>
            <div style={{ height: 1, background: 'var(--grey-100)', margin: '10px 0 20px' }} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="form-group">
                <label className="form-label">
                  <Icon d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
                  Start Date
                </label>
                <input
                  className={`form-input${errors.start_date ? ' error' : ''}`}
                  type="date" value={form.start_date} onChange={set('start_date')}
                />
                {errors.start_date && <span className="form-error">{errors.start_date}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">
                  <Icon d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
                  End Date
                </label>
                <input
                  className={`form-input${errors.end_date ? ' error' : ''}`}
                  type="date" value={form.end_date} onChange={set('end_date')}
                />
                {errors.end_date && <span className="form-error">{errors.end_date}</span>}
              </div>
            </div>
          </div>

          {/* Section 2 */}
          <div>
            <div className="form-section-title">Eligibility Criteria</div>
            <div style={{ height: 1, background: 'var(--grey-100)', margin: '10px 0 20px' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Field label="Eligibility Cut-off Months" name="eligibility_cutoff_months"
                icon="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
              <Field label="Lower Cut-off Average Score" name="lower_cutoff_avg_score"
                icon="M18 20V10M12 20V4M6 20v-6" />
              <Field label="Increment Factor" name="increment_factor"
                icon="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 6v6l4 2" />
            </div>
          </div>

          {/* Section 3 */}
          <div>
            <div className="form-section-title">CTC Thresholds</div>
            <div style={{ height: 1, background: 'var(--grey-100)', margin: '10px 0 20px' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Field label="Low Salary Compensation CTC Limit" name="low_sal_comp_ctc_limit"
                icon="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              <Field label="High Salary Reduction CTC Limit" name="high_sal_reduction_ctc_limit"
                icon="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </div>
          </div>

          {/* Section 4 */}
          <div>
            <div className="form-section-title">Salary Adjustment Factors</div>
            <div style={{ height: 1, background: 'var(--grey-100)', margin: '10px 0 20px' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Field label="Low Salary Compensation Factor" name="low_sal_comp_factor"
                icon="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              <Field label="High Salary Reduction Factor" name="high_sal_reduction_factor"
                icon="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              <Field label="Low Salary Compensation Score CutOff" name="low_sal_comp_score_cutoff"
                icon="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
            style={{ width: '100%', justifyContent: 'center', padding: 14, fontSize: 15, marginTop: 4 }}
          >
            {loading ? <><span className="spinner" style={{ width: 16, height: 16 }} /> Saving…</> : 'Save'}
          </button>
        </form>
      </div>
    </div>
  );
}
